import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/styles/Notificationswebadmin.css";

// LeftNav / TopNav - keep your actual imports
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";

// image assets (ensure these files exist at these paths)
import ellipsewedadmin from "../assets/img/ellipsenotiadmin.svg";
import newuserweb from "../assets/img/newuserweb.svg";
import subendweb from "../assets/img/subendweb.svg";
import Crmactivity from "../assets/img/crmactivityweb.svg";
import newsubweb from "../assets/img/newsubweb.svg";

// Import notification service - matching AdminNotification.js
import notificationService from "../../services/notificationService";

export default function NotificationsFullPage() {
  const navigate = useNavigate();

  // API Integration States - matching AdminNotification.js
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);

  // Modal states for expanded notifications - matching AdminNotification.js
  const [expandedNotification, setExpandedNotification] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [leadRegistrationModalVisible, setLeadRegistrationModalVisible] = useState(false);
  const [assignLeadModalVisible, setAssignLeadModalVisible] = useState(false);
  const [formData, setFormData] = useState({ field1: '', field2: '', field3: '' });
  const [selectedUser, setSelectedUser] = useState('Select User');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);

  // Load notifications on component mount - exact same flow as AdminNotification.js
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await notificationService.getNotifications();

      if (response.success) {
        const notificationData = response.data?.notifications || response.data || [];
        const formattedNotifications = notificationData.map(notification => ({
          id: notification.id,
          section: getDateSection(notification.created_at),
          image: getNotificationImage(notification.type),
          color: getNotificationColor(notification.type),
          title: notification.title || 'Notification',
          subtitle: notification.message || '',
          time: getTimeAgo(notification.created_at),
          unread: !notification.read_at,
        }));
        setNotifications(formattedNotifications);
      } else {
        setError('Failed to load notifications');
      }
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadNotifications();
    setRefreshing(false);
  };

  // Helper functions - exact same as AdminNotification.js
  const getDateSection = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-GB');
    }
  };

  const getNotificationImage = (type) => {
    const imageMap = {
      'property_inquiry': newuserweb,
      'lead_assigned': newuserweb,
      'task_reminder': Crmactivity,
      'payment_success': newsubweb,
      'payment_failed': subendweb,
      'subscription_expiry': subendweb,
      'welcome': newuserweb,
      'verification': newuserweb,
      'general': Crmactivity,
    };
    return imageMap[type] || newuserweb;
  };

  const getNotificationColor = (type) => {
    const colorMap = {
      'property_inquiry': '#42A5F5',
      'lead_assigned': '#FFB300',
      'task_reminder': '#FF7043',
      'payment_success': '#4CAF50',
      'payment_failed': '#EF5350',
      'subscription_expiry': '#FFB300',
      'welcome': '#4CAF50',
      'verification': '#4CAF50',
      'general': '#42A5F5',
    };
    return colorMap[type] || '#42A5F5';
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleNotificationPress = async (notification) => {
    console.log("Notification pressed:", notification.id);

    // Mark as read if unread
    if (notification.unread) {
      await markAsRead(notification.id);
    }

    if (notification.id === 8) {
      setExpandedNotification(expandedNotification === 8 ? null : 8);
    } else if (notification.id === 10) {
      setLeadRegistrationModalVisible(true);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, unread: false } : n
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      // Update all notifications to mark them as read
      setNotifications(notifications.map(n => ({ ...n, unread: false })));
      setMenuVisible(false);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  // Lead Modal handlers - matching AdminNotification.js
  const handleAcceptLead = () => {
    setLeadRegistrationModalVisible(false);
    setAssignLeadModalVisible(true);
  };

  const handleRejectLead = () => {
    setLeadRegistrationModalVisible(false);
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setIsDropdownVisible(false);
  };

  const handleAssignLead = () => {
    console.log('Lead assigned to:', selectedUser);
    setAssignLeadModalVisible(false);
    setSelectedUser('Select User');
    setIsTermsChecked(false);
  };

  const handleCancelAssign = () => {
    setAssignLeadModalVisible(false);
    setSelectedUser('Select User');
    setIsTermsChecked(false);
  };

  const handleSubmit = () => {
    console.log("Form submitted:", formData);
    setModalVisible(false);
  };

  return (
    <div className="ntf-page d-flex">
      <LeftNav />

      <div className="ntf-content-wrap flex-grow-1">
        <TopNav />

        <div className="container-fluid ntf-outer">
          <div className="card ntf-container-card mt-3">
            <div className="card-body">

              {/* Header: title + 3-dot menu */}
              <div className="d-flex justify-content-between align-items-start ntf-header-row">
                <div className="d-flex align-items-center gap-2">
                  <button className="btn ntf-back-btn" type="button" onClick={() => navigate(-1)}>←</button>
                  <h2 className="ntf-title mb-0">Notifications</h2>
                </div>

                <div style={{ position: 'relative' }}>
                  <button
                    className="btn btn-outline-secondary ntf-more-top"
                    title="More"
                    type="button"
                    onClick={() => setMenuVisible(!menuVisible)}
                  >
                    {ellipsewedadmin && <img src={ellipsewedadmin} alt="more" className="ntf-3dot" />}
                  </button>

                  {/* Dropdown menu - matching AdminNotification.js */}
                  {menuVisible && (
                    <div style={{
                      position: 'absolute',
                      right: '0',
                      top: '100%',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '150px',
                      marginTop: '5px'
                    }}>
                      <button
                        className="btn"
                        style={{
                          width: '100%',
                          padding: '10px 15px',
                          backgroundColor: '#42A5F5',
                          color: '#fff',
                          borderRadius: '8px',
                          border: 'none',
                          fontWeight: 'bold'
                        }}
                        onClick={handleMarkAllRead}
                      >
                        Mark all as Read
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Hero / Contact support */}
              <div className="ntf-hero-card mt-3">
                <div className="ntf-hero-inner">
                  <h5 className="ntf-hero-title">Need Help Choosing?</h5>
                  <p className="ntf-hero-sub">Our team is ready to assist you in selecting the best plan for your business needs.</p>
                  <button className="btn ntf-contact-btn" type="button">Contact support</button>
                </div>
              </div>

              {/* Loading state - matching AdminNotification.js */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#EF5350', marginBottom: '10px' }}>{error}</p>
                  <button
                    className="btn btn-primary"
                    onClick={loadNotifications}
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div>
                  {/* Refresh indicator */}
                  {refreshing && (
                    <div style={{ textAlign: 'center', padding: '10px' }}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Refreshing...</span>
                      </div>
                    </div>
                  )}

                  {/* Dynamic notification sections - matching AdminNotification.js */}
                  {notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                      <p style={{ color: '#666', fontSize: '16px' }}>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification, index) => (
                      <React.Fragment key={notification.id}>
                        {/* Section headers - show when section changes */}
                        {(index === 0 || notifications[index - 1].section !== notification.section) && (
                          <div className="ntf-group mt-4 mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2 ntf-group-header">
                              <strong>{notification.section}</strong>
                              {/* Mark All Read - only show in first section */}
                              {index === 0 && (
                                <button
                                  className="btn btn-sm ntf-markall-btn"
                                  onClick={handleMarkAllRead}
                                  type="button"
                                >
                                  Mark All Read
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Dynamic Notification Item */}
                        <div
                          className={`ntf-item card mb-2 ${expandedNotification === notification.id ? 'expanded' : ''}`}
                          onClick={() => handleNotificationPress(notification)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="card-body d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3 ntf-left">
                              <div className="ntf-icon-wrap">
                                <img src={notification.image} alt="" className="ntf-item-icon" />
                              </div>
                              <div className="ntf-item-content">
                                <div className={`ntf-item-title ${expandedNotification === notification.id ? 'expanded' : ''}`}>
                                  {notification.title}
                                </div>
                                <div className="ntf-item-sub">{notification.subtitle}</div>
                              </div>
                            </div>

                            <div className="ntf-right">
                              <div className="ntf-time">
                                {notification.unread && (
                                  <span
                                    className="ntf-dot"
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      background: notification.color,
                                      display: "inline-block",
                                      marginRight: 8
                                    }}
                                  />
                                )}
                                {notification.time}
                              </div>
                            </div>
                          </div>

                          {/* Expanded Content for Agent Registration - matching AdminNotification.js */}
                          {expandedNotification === notification.id && notification.id === 8 && (
                            <div style={{
                              backgroundColor: '#f8f9fa',
                              padding: '16px',
                              marginTop: '8px',
                              borderRadius: '8px',
                              margin: '8px'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '12px'
                              }}>
                                <h6 style={{ color: '#6366f1', fontWeight: 'bold', margin: 0 }}>
                                  New Agent Registration
                                </h6>
                                <button
                                  className="btn btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedNotification(null);
                                  }}
                                  style={{ backgroundColor: 'transparent', border: 'none' }}
                                >
                                  ✕
                                </button>
                              </div>

                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', marginBottom: '8px' }}>
                                  <strong style={{ width: '60px' }}>Name:</strong>
                                  <span>John Smith</span>
                                </div>
                                <div style={{
                                  display: 'flex',
                                  marginBottom: '8px',
                                  alignItems: 'center',
                                  justifyContent: 'space-between'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <strong style={{ width: '60px' }}>City:</strong>
                                    <span>Hyderabad</span>
                                  </div>
                                  <button
                                    className="btn btn-sm btn-dark"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                  >
                                    View Profile
                                  </button>
                                </div>
                              </div>

                              <textarea
                                className="form-control"
                                placeholder="Enter reason for rejection"
                                rows="3"
                                style={{ marginBottom: '16px' }}
                              ></textarea>

                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '12px'
                              }}>
                                <button
                                  className="btn btn-outline-secondary"
                                  style={{ flex: 1 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Agent rejected');
                                    setExpandedNotification(null);
                                  }}
                                >
                                  Reject
                                </button>
                                <button
                                  className="btn btn-primary"
                                  style={{ flex: 1, backgroundColor: '#6366f1' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Agent accepted');
                                    setExpandedNotification(null);
                                  }}
                                >
                                  Accept
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Modals matching AdminNotification.js functionality */}

      {/* Basic Modal */}
      {modalVisible && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enter Details</h5>
                <button type="button" className="btn-close" onClick={() => setModalVisible(false)}></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Field 1"
                  value={formData.field1}
                  onChange={(e) => setFormData({ ...formData, field1: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Field 2"
                  value={formData.field2}
                  onChange={(e) => setFormData({ ...formData, field2: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Field 3"
                  value={formData.field3}
                  onChange={(e) => setFormData({ ...formData, field3: e.target.value })}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalVisible(false)}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Registration Modal */}
      {leadRegistrationModalVisible && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'linear-gradient(135deg, #F2BC4A, #F9D78D)' }}>
              <div className="modal-header border-0">
                <button type="button" className="btn-close" onClick={() => setLeadRegistrationModalVisible(false)}></button>
              </div>
              <div className="modal-body text-center">
                <h4 className="mb-2" style={{ color: '#000', fontWeight: 'bold' }}>New Lead Registration</h4>
                <p className="mb-3" style={{ color: '#333' }}>Registered By Travis Head</p>

                <button className="btn btn-light mb-4" style={{ color: '#000', fontWeight: '600' }}>
                  View Profile
                </button>

                <div className="mb-4">
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <hr style={{ flex: 1, border: '1px solid #000' }} />
                    <span style={{ padding: '0 10px', fontWeight: '600', color: '#000' }}>Lead Details</span>
                    <hr style={{ flex: 1, border: '1px solid #000' }} />
                  </div>
                </div>

                <div className="text-start mb-4" style={{ color: '#000' }}>
                  <p>Name : John Smith</p>
                  <p>Ph No : *****9008</p>
                  <p>Email Id : John*****@gmail.com</p>
                  <p>Project : HR Colony Residence</p>
                  <p>City : Hyderabad</p>
                </div>

                <textarea
                  className="form-control mb-4"
                  placeholder="Enter reason for rejection"
                  rows="4"
                  style={{ borderColor: '#ddd' }}
                ></textarea>

                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      border: '1px solid #000',
                      color: '#000',
                      fontWeight: 'bold'
                    }}
                    onClick={handleRejectLead}
                  >
                    REJECT
                  </button>
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      backgroundColor: '#333',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                    onClick={handleAcceptLead}
                  >
                    ACCEPT
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Lead Modal */}
      {assignLeadModalVisible && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'linear-gradient(135deg, #F2BC4A, #F9D78D)' }}>
              <div className="modal-header border-0">
                <button type="button" className="btn-close" onClick={() => setAssignLeadModalVisible(false)}></button>
              </div>
              <div className="modal-body">
                <h4 className="text-center mb-4" style={{ color: '#000', fontWeight: 'bold' }}>
                  Assign Registered Lead
                </h4>

                <div className="mb-4">
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <hr style={{ flex: 1, border: '1px solid #000' }} />
                    <span style={{ padding: '0 10px', fontWeight: '600', color: '#000' }}>Lead Details</span>
                    <hr style={{ flex: 1, border: '1px solid #000' }} />
                  </div>
                </div>

                <div className="mb-4" style={{ color: '#000' }}>
                  <p>Lead ID : 8799978</p>
                  <p>Name : John Smith</p>
                  <p>Ph No : 9949430903</p>
                  <p>Email ID : Johnsmith44@gmail.com</p>
                  <p>Project : HR Colony Residence</p>
                  <p>City : Hyderabad</p>
                </div>

                <h6 style={{ color: '#000', fontWeight: '600', marginBottom: '10px' }}>
                  Assign Lead To
                </h6>

                <div className="dropdown mb-4">
                  <button
                    className="btn btn-light dropdown-toggle w-100 text-start"
                    type="button"
                    onClick={() => setIsDropdownVisible(!isDropdownVisible)}
                    style={{ borderColor: '#ddd' }}
                  >
                    {selectedUser}
                  </button>
                  {isDropdownVisible && (
                    <ul className="dropdown-menu show w-100" style={{ position: 'relative' }}>
                      {['User 1', 'User 2', 'User 3', 'User 4', 'User 5'].map((user, index) => (
                        <li key={index}>
                          <button
                            className="dropdown-item"
                            onClick={() => handleUserSelect(user)}
                          >
                            {user}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={isTermsChecked}
                    onChange={(e) => setIsTermsChecked(e.target.checked)}
                    style={{ backgroundColor: isTermsChecked ? '#4CAF50' : '#fff' }}
                  />
                  <label className="form-check-label" style={{ color: '#000' }}>
                    Terms and Conditions
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '15px' }}>
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      backgroundColor: 'transparent',
                      border: '1px solid #000',
                      color: '#000',
                      fontWeight: 'bold'
                    }}
                    onClick={handleCancelAssign}
                  >
                    CANCEL
                  </button>
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      backgroundColor: '#333',
                      border: 'none',
                      color: '#fff',
                      fontWeight: 'bold'
                    }}
                    onClick={handleAssignLead}
                  >
                    ASSIGN
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
